import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react'
import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import { API_BASE_URL } from '../utils/api'
import { 
  startRegistration, 
  startAuthentication
} from '@simplewebauthn/browser'
import { addRecentAccount, updateRecentAccountPasskeyStatus } from '../utils/recentAccounts'

interface User {
  id: string
  email: string
  name: string
  firstName?: string
  lastName?: string
  phone?: string
  role: 'admin' | 'broker' | 'collaborator'
  language?: string
}

interface Passkey {
  id: number
  deviceName: string
  backupEligible: boolean
  backupState: boolean
  lastUsedAt: string | null
  createdAt: string
}

interface SecurityStatus {
  hasPasskeys: boolean
  hasTwoFactor: boolean
  hasAnySecurityFeatures: boolean
  needsSecuritySetup: boolean
}

interface AuthState {
  user: User | null
  token: string | null
  loading: boolean
  error: string | null
  securityStatus: SecurityStatus | null
  pendingVerification: boolean
}

type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'CLEAR_LOADING' }
  | { type: 'PENDING_VERIFICATION' }
  | { type: 'UPDATE_USER'; payload: Partial<User> }
  | { type: 'SET_SECURITY_STATUS'; payload: SecurityStatus }

interface PendingAuthResponse {
  requiresVerification: boolean
  sessionToken: string
  has2FA: boolean
  hasSMS2FA: boolean
  hasPasskeys: boolean
  user: any
}

interface AuthContextType {
  user: User | null
  token: string | null
  loading: boolean
  error: string | null
  securityStatus: SecurityStatus | null
  login: (email: string, password: string) => Promise<PendingAuthResponse | null>
  loginWithPasskey: () => Promise<void>
  loginWithEmail: (email: string) => Promise<void>
  registerPasskey: (deviceName?: string) => Promise<boolean>
  getPasskeys: () => Promise<Passkey[]>
  deletePasskey: (passkeyId: number) => Promise<void>
  supportsPasskeys: boolean
  checkSecurityStatus: (retryCount?: number) => Promise<void>
  logout: () => void
  clearError: () => void
  updateUser: (updates: Partial<User>) => void
  setAuthTokens: (token: string, refreshToken: string, user: any) => void
  isAdmin: boolean
  isBroker: boolean
  isCollaborator: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, loading: true, error: null }
    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        loading: false,
        error: null,
        pendingVerification: false,
      }
    case 'AUTH_ERROR':
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: action.payload,
        pendingVerification: false,
      }
    case 'AUTH_LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        loading: false,
        error: null,
        pendingVerification: false,
      }
    case 'CLEAR_ERROR':
      return { ...state, error: null }
    case 'CLEAR_LOADING':
      return { ...state, loading: false, error: null }
    case 'PENDING_VERIFICATION':
      return { ...state, loading: false, error: null, pendingVerification: true }
    case 'UPDATE_USER':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload } : null
      }
    case 'SET_SECURITY_STATUS':
      return {
        ...state,
        securityStatus: action.payload
      }
    default:
      return state
  }
}

const initialState: AuthState = {
  user: null,
  token: null,
  loading: true,
  error: null,
  securityStatus: null,
  pendingVerification: false,
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState)

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      try {
        const decoded = jwtDecode<User & { exp: number }>(token)
        
        if (decoded.exp * 1000 > Date.now()) {
          // Token is still valid
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: {
              user: {
                id: decoded.id,
                email: decoded.email,
                name: decoded.name,
                firstName: decoded.firstName,
                lastName: decoded.lastName,
                role: decoded.role || 'broker', // Default to broker for backward compatibility
                language: decoded.language,
              },
              token,
            },
          })
        } else {
          // Token expired
          localStorage.removeItem('token')
          dispatch({ type: 'AUTH_LOGOUT' })
        }
      } catch (error) {
        localStorage.removeItem('token')
        dispatch({ type: 'AUTH_LOGOUT' })
      }
    } else {
      dispatch({ type: 'AUTH_LOGOUT' })
    }

    // Listen for token expiration events from API calls
    const handleTokenExpired = () => {
      localStorage.removeItem('token')
      delete axios.defaults.headers.common['Authorization']
      dispatch({ type: 'AUTH_LOGOUT' })
    }

    window.addEventListener('tokenExpired', handleTokenExpired)

    // Add axios interceptor for 401 responses
    const axiosInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          handleTokenExpired()
          alert('Your session has expired. Please log in again.')
          window.location.href = '/login'
        }
        return Promise.reject(error)
      }
    )
    
    return () => {
      window.removeEventListener('tokenExpired', handleTokenExpired)
      axios.interceptors.response.eject(axiosInterceptor)
    }
  }, [])

  const login = async (email: string, password: string): Promise<PendingAuthResponse | null> => {
    dispatch({ type: 'AUTH_START' })
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, { email, password })
      const data = response.data

      // Check if backend requires additional verification
      if (data.requiresVerification) {
        // Set pending verification state - clears loading but keeps on login page
        dispatch({ type: 'PENDING_VERIFICATION' })
        const pendingResponse = {
          requiresVerification: data.requiresVerification,
          sessionToken: data.sessionToken,
          has2FA: data.has2FA,
          hasSMS2FA: data.hasSMS2FA,
          hasPasskeys: data.hasPasskeys,
          user: data.user
        }
        return pendingResponse
      }

      // Check if user needs security setup
      if (data.needsSecuritySetup && data.securitySetupStatus) {
        // Store token first so user is authenticated
        const { token, user } = data
        localStorage.setItem('token', token)
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

        const userObj = {
          id: user.id.toString(),
          email: user.email,
          name: user.name,
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role || 'broker',
          language: user.language,
        }

        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: userObj,
            token,
          },
        })

        // Return a special response indicating setup is needed
        return {
          requiresVerification: false,
          needsSecuritySetup: true,
          securitySetupStatus: data.securitySetupStatus,
          sessionToken: '',
          has2FA: false,
          hasSMS2FA: false,
          hasPasskeys: false,
          user: data.user
        } as any
      }

      // Legacy flow: full authentication complete
      const { token, user } = data

      localStorage.setItem('token', token)
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

      const userObj = {
        id: user.id.toString(),
        email: user.email,
        name: user.name,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role || 'broker', // Default to broker for backward compatibility
        language: user.language,
      }

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: userObj,
          token,
        },
      })

      // Save to recent accounts
      addRecentAccount({
        id: userObj.id,
        email: userObj.email,
        firstName: userObj.firstName,
        lastName: userObj.lastName,
        profilePictureUrl: user.profile_picture_url,
        hasPasskey: false // Will be updated when we check passkey status
      })

      return null
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Login failed'
      
      dispatch({ type: 'AUTH_ERROR', payload: errorMessage })
      throw error
    }
  }

  const setAuthTokens = (token: string, refreshToken: string, user: any) => {
    localStorage.setItem('token', token)
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

    const userObj = {
      id: user.id.toString(),
      email: user.email,
      name: user.name,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role || 'broker',
      language: user.language,
    }

    dispatch({
      type: 'AUTH_SUCCESS',
      payload: {
        user: userObj,
        token,
      },
    })

    // Save to recent accounts
    addRecentAccount({
      id: userObj.id,
      email: userObj.email,
      firstName: userObj.firstName,
      lastName: userObj.lastName,
      profilePictureUrl: user.profile_picture_url,
      hasPasskey: false // Will be updated when we check passkey status
    })
  }

  const logout = () => {
    localStorage.removeItem('token')
    delete axios.defaults.headers.common['Authorization']
    dispatch({ type: 'AUTH_LOGOUT' })
  }

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' })
  }

  const updateUser = (updates: Partial<User>) => {
    dispatch({ type: 'UPDATE_USER', payload: updates })
  }

  // Check if browser supports WebAuthn
  const supportsPasskeys = typeof window !== 'undefined' && 
    window.PublicKeyCredential && 
    typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function'

    const loginWithPasskey = async () => {
    dispatch({ type: 'AUTH_START' })
    try {
      // Step 1: Get authentication options from server
      const optionsResponse = await axios.post(`${API_BASE_URL}/api/auth/passkey/authenticate/begin`)
      const options = optionsResponse.data
      
      // Convert challenge from base64url to ArrayBuffer for native API
      const challenge = Uint8Array.from(atob(options.challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0))
      
      // Use native WebAuthn API for discoverable credentials (no email needed!)
      const credentialResponse = await navigator.credentials.get({
        publicKey: {
          challenge: challenge,
          timeout: options.timeout || 60000,
          rpId: options.rpId || window.location.hostname,
          userVerification: options.userVerification || "preferred"
          // No allowCredentials - let browser show all available passkeys
        }
      })

      if (!credentialResponse) {
        throw new Error('No passkey selected')
      }

      // Convert credential response for backend
      const credential = credentialResponse as PublicKeyCredential
      const response = credential.response as AuthenticatorAssertionResponse
      
      const arrayBufferToBase64url = (buffer: ArrayBuffer) => {
        const bytes = new Uint8Array(buffer)
        const base64 = btoa(String.fromCharCode(...bytes))
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
      }

      const credentialForBackend = {
        id: credential.id,
        rawId: arrayBufferToBase64url(credential.rawId),
        response: {
          clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
          authenticatorData: arrayBufferToBase64url(response.authenticatorData),
          signature: arrayBufferToBase64url(response.signature),
          userHandle: response.userHandle ? arrayBufferToBase64url(response.userHandle) : null,
        },
        type: credential.type,
      }

      // Send credential to backend for verification
      const verificationResponse = await axios.post(`${API_BASE_URL}/api/auth/passkey/authenticate/finish`, {
        body: credentialForBackend
      })

      const { token, user } = verificationResponse.data

      localStorage.setItem('token', token)
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

      const userObj = {
        id: user.id.toString(),
        email: user.email,
        name: user.name,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role || 'broker',
        language: user.language,
      }

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: userObj,
          token,
        },
      })

      // Save to recent accounts (passkey users definitely have passkeys)
      addRecentAccount({
        id: userObj.id,
        email: userObj.email,
        firstName: userObj.firstName,
        lastName: userObj.lastName,
        profilePictureUrl: user.profile_picture_url,
        hasPasskey: true
      })
    } catch (error: any) {
      // Check if user canceled the passkey prompt
      const isCancellation = 
        error.name === 'NotAllowedError' || 
        error.name === 'AbortError' ||
        (error.message && (
          error.message.includes('cancel') ||
          error.message.includes('abort') ||
          error.message.includes('user denied')
        ))

      if (isCancellation) {
        // User canceled - just clear loading state, no error message
        dispatch({ type: 'CLEAR_ERROR' })
        throw new Error('PASSKEY_CANCELED')
      }

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Passkey authentication failed'

      dispatch({ type: 'AUTH_ERROR', payload: errorMessage })
      throw error
    }
  }

  // Login with email - attempts passkey first if available
  const loginWithEmail = async (email: string) => {
    dispatch({ type: 'AUTH_START' })
    try {
      // Check if this user has passkeys by attempting passkey login
      if (supportsPasskeys) {
        try {
          // Step 1: Get authentication options from server for this specific email
          const optionsResponse = await axios.post(`${API_BASE_URL}/api/auth/passkey/authenticate/begin`, { email })
          const options = optionsResponse.data
          
          // Convert challenge from base64url to ArrayBuffer for native API
          const challenge = Uint8Array.from(atob(options.challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0))
          
          // Build allowCredentials array for user's specific passkeys
          let allowCredentials: any[] = []
          if (options.allowCredentials && options.allowCredentials.length > 0) {
            allowCredentials = options.allowCredentials.map((cred: any) => {
              // Convert base64url string to Uint8Array (Safari-compatible)
              const base64url = cred.id
              // Convert base64url to base64
              const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
              // Decode to binary string
              const binaryString = atob(base64)
              // Convert to Uint8Array
              const bytes = new Uint8Array(binaryString.length)
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i)
              }
              
              return {
                id: bytes,
                type: 'public-key' as const,
              }
            })
          }
          
          // Use native WebAuthn API with user's specific credentials
          const credentialResponse = await navigator.credentials.get({
            publicKey: {
              challenge: challenge,
              timeout: options.timeout || 60000,
              rpId: options.rpId || window.location.hostname,
              userVerification: options.userVerification || "preferred",
              allowCredentials: allowCredentials.length > 0 ? allowCredentials : undefined
            }
          })

          if (!credentialResponse) {
            throw new Error('No passkey selected or authentication cancelled')
          }

          // Convert credential response for backend
          const credential = credentialResponse as PublicKeyCredential
          const response = credential.response as AuthenticatorAssertionResponse
          
          const arrayBufferToBase64url = (buffer: ArrayBuffer) => {
            const bytes = new Uint8Array(buffer)
            const base64 = btoa(String.fromCharCode(...bytes))
            return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
          }

          const credentialForBackend = {
            id: credential.id,
            rawId: arrayBufferToBase64url(credential.rawId),
            response: {
              clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
              authenticatorData: arrayBufferToBase64url(response.authenticatorData),
              signature: arrayBufferToBase64url(response.signature),
              userHandle: response.userHandle ? arrayBufferToBase64url(response.userHandle) : null,
            },
            type: credential.type,
          }

          // Step 3: Send credential to backend for verification (using same structure as loginWithPasskey)
          const verificationResponse = await axios.post(`${API_BASE_URL}/api/auth/passkey/authenticate/finish`, {
            body: credentialForBackend
          })

          const { token, user } = verificationResponse.data

          localStorage.setItem('token', token)
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

          const userObj = {
            id: user.id.toString(),
            email: user.email,
            name: user.name,
            firstName: user.firstName,
            lastName: user.lastName,
            role: user.role || 'broker',
            language: user.language,
          }

          dispatch({
            type: 'AUTH_SUCCESS',
            payload: {
              user: userObj,
              token,
            },
          })

          // Save to recent accounts (passkey users definitely have passkeys)
          addRecentAccount({
            id: userObj.id,
            email: userObj.email,
            firstName: userObj.firstName,
            lastName: userObj.lastName,
            profilePictureUrl: user.profile_picture_url,
            hasPasskey: true
          })

          return // Success with passkey
        } catch (passkeyError: any) {
          // Check if user canceled the passkey prompt
          const isCancellation = 
            passkeyError.name === 'NotAllowedError' || 
            passkeyError.name === 'AbortError' ||
            (passkeyError.message && (
              passkeyError.message.includes('cancel') ||
              passkeyError.message.includes('abort') ||
              passkeyError.message.includes('user denied')
            ))

          if (isCancellation) {
            // User canceled - throw special error without showing message
            throw new Error('PASSKEY_CANCELED')
          }

          // If user has no passkeys or the error indicates no passkeys, show helpful message
          // Check both axios error structure and response data
          if (passkeyError.response?.status === 404 || 
              passkeyError.response?.data?.error === 'No passkeys found' ||
              passkeyError.response?.data?.message?.includes('No passkeys') ||
              (passkeyError.message && passkeyError.message.includes('404'))) {
            throw new Error('This account doesn\'t have any passkeys set up. Please enter your password to continue.')
          }
          
          // For other errors, show generic message
          throw new Error('Please enter your password to continue')
        }
      }
      
      // If we get here, passkey login failed or is not supported
      throw new Error('Please enter your password to continue')
      
    } catch (error: any) {
      // Don't show error for user cancellation
      if (error.message === 'PASSKEY_CANCELED') {
        dispatch({ type: 'CLEAR_ERROR' })
        throw error
      }

      const errorMessage =
        error.message ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Login failed'

      dispatch({ type: 'AUTH_ERROR', payload: errorMessage })
      throw error
    }
  }

  const registerPasskey = async (deviceName?: string) => {
    if (!state.user) {
      throw new Error('User must be logged in to register a passkey')
    }

    try {
      // Step 1: Get registration options from server
      const optionsResponse = await axios.post(`${API_BASE_URL}/api/auth/passkey/register/begin`, {}, {
        headers: { Authorization: `Bearer ${state.token}` }
      })
      const options = optionsResponse.data

      // Convert base64url strings to ArrayBuffers for native API
      const challenge = Uint8Array.from(atob(options.challenge.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0))
      const userId = Uint8Array.from(atob(options.user.id.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0))
      
      const publicKeyCredentialCreationOptions = {
        challenge: challenge,
        rp: {
          name: options.rp.name,
          id: options.rp.id,
        },
        user: {
          id: userId,
          name: options.user.name,
          displayName: options.user.displayName,
        },
        pubKeyCredParams: options.pubKeyCredParams,
        authenticatorSelection: {
          ...options.authenticatorSelection,
          requireResidentKey: false, // Don't force discoverable credentials  
          residentKey: "preferred", // Prefer but don't require resident key
          userVerification: "preferred" // More permissive verification
        },
        timeout: options.timeout,
        attestation: options.attestation
      }

      const credentialResponse = await navigator.credentials.create({
        publicKey: publicKeyCredentialCreationOptions
      })

      if (!credentialResponse) {
        throw new Error('Failed to create credential')
      }

      // Convert the credential to the format expected by backend (base64url strings)
      const credential = credentialResponse as PublicKeyCredential
      const response = credential.response as AuthenticatorAttestationResponse
      
      // Convert ArrayBuffers to base64url strings
      const arrayBufferToBase64url = (buffer: ArrayBuffer) => {
        const bytes = new Uint8Array(buffer)
        const base64 = btoa(String.fromCharCode(...bytes))
        return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
      }
      
      const credentialForBackend = {
        id: credential.id, // Already a string
        rawId: arrayBufferToBase64url(credential.rawId),
        response: {
          clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
          attestationObject: arrayBufferToBase64url(response.attestationObject),
        },
        type: credential.type,
      }

      // Step 3: Send credential to server for verification
      await axios.post(`${API_BASE_URL}/api/auth/passkey/register/finish`, {
        body: credentialForBackend,
        deviceName
      }, {
        headers: { Authorization: `Bearer ${state.token}` }
      })

      return true
        } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to register passkey'

      throw new Error(errorMessage)
    }
  }

  const getPasskeys = async (): Promise<Passkey[]> => {
    if (!state.user || !state.token) {
      throw new Error('User must be logged in to get passkeys')
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/passkeys`, {
        headers: { Authorization: `Bearer ${state.token}` }
      })
      
      return response.data.passkeys
    } catch (error: any) {
      const errorMessage = 
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to get passkeys'
      
      throw new Error(errorMessage)
    }
  }

  const deletePasskey = async (passkeyId: number) => {
    if (!state.user || !state.token) {
      throw new Error('User must be logged in to delete passkeys')
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/auth/passkeys/${passkeyId}`, {
        headers: { Authorization: `Bearer ${state.token}` }
      })
    } catch (error: any) {
      const errorMessage = 
        error.response?.data?.message ||
        error.response?.data?.error ||
        'Failed to delete passkey'
      
      throw new Error(errorMessage)
    }
  }

  const checkSecurityStatus = async (retryCount = 0) => {
    if (!state.user || !state.token) {
      return
    }

    try {
      // Check for passkeys
      let hasPasskeys = false
      if (supportsPasskeys) {
        try {
          const passkeys = await getPasskeys()
          hasPasskeys = passkeys.length > 0
        } catch (err) {
          // Silently fail
        }
      }
      
      // Check for 2FA
      let has2FA = false
      try {
        const response = await axios.get(`${API_BASE_URL}/api/2fa/status`, {
          headers: { Authorization: `Bearer ${state.token}` }
        })
        if (response.data) {
          has2FA = response.data.enabled
        }
      } catch (err) {
        // Silently fail
      }
      
      // Check for SMS 2FA
      let hasSMS2FA = false
      try {
        const response = await axios.get(`${API_BASE_URL}/api/sms2fa/status`, {
          headers: { Authorization: `Bearer ${state.token}` }
        })
        if (response.data) {
          hasSMS2FA = response.data.enabled
        }
      } catch (err) {
        // Silently fail
      }
      
      // Check if user needs security setup from backend
      let needsSecuritySetup = false
      try {
        const setupResponse = await axios.get(`${API_BASE_URL}/api/auth/security-setup-status`, {
          headers: { Authorization: `Bearer ${state.token}` }
        })
        if (setupResponse.data) {
          needsSecuritySetup = setupResponse.data.requiresSetup || false
        }
      } catch (err) {
        // Silently fail
      }
      
      const securityStatus: SecurityStatus = {
        hasPasskeys,
        hasTwoFactor: has2FA || hasSMS2FA,
        hasAnySecurityFeatures: hasPasskeys || has2FA || hasSMS2FA,
        needsSecuritySetup: needsSecuritySetup && !hasPasskeys && !has2FA && !hasSMS2FA
      }

      dispatch({ type: 'SET_SECURITY_STATUS', payload: securityStatus })
    } catch (error) {
      // Retry logic for network errors
      if (retryCount < 3) {
        setTimeout(() => checkSecurityStatus(retryCount + 1), 2000)
      }
    }
  }

  const contextValue: AuthContextType = {
    user: state.user,
    token: state.token,
    loading: state.loading,
    error: state.error,
    securityStatus: state.securityStatus,
    login,
    loginWithPasskey,
    loginWithEmail,
    registerPasskey,
    getPasskeys,
    deletePasskey,
    supportsPasskeys,
    checkSecurityStatus,
    logout,
    clearError,
    updateUser,
    setAuthTokens,
    isAdmin: state.user?.role === 'admin',
    isBroker: state.user?.role === 'broker',
    isCollaborator: state.user?.role === 'collaborator',
  }



  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
} 